import { UserInfo, ConversationRequest, Conversation, ChatMessage, CosmosDBHealth, CosmosDBStatus, MessageType, RAGMenuItem, PromptTemplateCategory, PromptTemplate, PromptTemplateSchema, ConversationWithError, APIError, ModelInfo, AsyncTaskStatus, TranscribeStatus, InfoItem, ChatResponse, TaskStatus, GeneratingConversationInfo, RAGSummaryInfo } from "./models";
import { chatHistorySampleData } from "../constants/chatHistory";
import { deepMapToObject } from "../utils";

export async function conversationGenerateStart(
    options: ConversationRequest, 
    files: File[], 
    model_index: number | string, 
    onError: (statusCode: number, message?: string) => void,
    convId?: string, 
    tools?: string[],
    toolArgs?: Map<string, Map<string, any>>,
    rag_ids?: number[],
    template_id?: number,
    system_prompt?: string,
): Promise<GeneratingConversationInfo|undefined> {
    const formData = new FormData();
    files.forEach((file, index) => {
        formData.append(`file${index}`, file);
    });
    if(convId){
        formData.append("conversation_id", convId);
    }
    formData.append("messages", JSON.stringify(options.messages));
    formData.append("tools", JSON.stringify(tools ?? []))
    formData.append("model_index", String(model_index))
    formData.append("rag_ids", JSON.stringify(rag_ids ?? []))
    if(template_id) {
        formData.append("template_id", String(template_id))
    }
    if(system_prompt) {
        formData.append("system_prompt", system_prompt)
    }
    if(toolArgs) {
        const serializedToolArgs = deepMapToObject(toolArgs);
        formData.append("tool_args", JSON.stringify(serializedToolArgs))
    }

    let status_code = 500
    let error_mes: undefined|string = undefined
    try{
        const response = await fetch("/api/conversation/generate", {
            method: "POST",
            body: formData,
        });
        const json = await response.json()
        if("error" in json){
            error_mes = json.error
        }
        status_code = response.status

        if (!response.ok || !response.body) {
            throw new Error()
        }

        return {
            task_id: json.task_id,
            history_metadata: json.history_metadata,
            file_messages: json.file_messages
        }
    }
    catch {
        onError(status_code, error_mes)
    }
}

export async function conversationStream(
    conv_task_id: string,
    onError: (statusCode: number, message?: string) => void,
    onProgress: (res: ChatResponse) => void,
    onComplete: (res: ChatResponse) => void,
): Promise<void> {
    const response = await fetch(`/api/conversation/stream/${conv_task_id}`, {
        method: "POST"
    });

    if (!response.ok || !response.body) {
        let additionalErrorMes = undefined
        try{
            const json = await response.json()
            if("error" in json){
                additionalErrorMes = json.error
            }
        }
        catch {}
        onError(response.status, additionalErrorMes)
        return
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let runningText = "";
    let result = {} as ChatResponse;

    const tryParseLines = () => {
        while (true) {
            const newlineIndex = runningText.indexOf("\n");
            if (newlineIndex < 0) {
                break;
            }
            const line = runningText.slice(0, newlineIndex);
            runningText = runningText.slice(newlineIndex + 1);
            if (line.trim() === "") {
                continue;
            }
            let parsed: ChatResponse;
            try {
                parsed = JSON.parse(line);
            }
            catch {
                // 不完全なJSON行を次チャンクまで保持する
                runningText = line + "\n" + runningText;
                break;
            }
            result = parsed;
            // errorを含む行やchoicesを持たない行はprogressではないため、onProgressに渡さない。
            // （errorはループ後にまとめてonErrorで処理する）
            if (!parsed.error && parsed.choices && parsed.choices.length > 0) {
                onProgress(parsed);
            }
        }
    };

    while (true) {
        const {done, value} = await reader.read();
        if (done) {
            runningText += decoder.decode();
            tryParseLines();
            break;
        }
        runningText += decoder.decode(value, { stream: true });
        tryParseLines();
    }

    // error:　が含まれている場合はエラーとして処理(status 200)
    if(result.error){
        onError(response.status, result.error)
        return;
    }

    onComplete(result)
}

export async function abortConversation(conv_task_id: string): Promise<boolean> {
    const response = await fetch(`/api/conversation/abort/${conv_task_id}`,{
        method: "POST"
    });
    if (!response.ok) {
        console.log("Conversation abortion failed.")
    }
    return response.ok
}

export async function getGeneratingConversations(): Promise<GeneratingConversationInfo[]> {
    const response = await fetch(`/api/conversation/generating`,{
        method: "GET"
    });
    if (!response.ok) {
        return [];
    }

    const payload = await response.json();
    return payload;
}

export async function getUserInfo(): Promise<UserInfo[]> {
    const response = await fetch('/.auth/me');
    if (!response.ok) {
        console.log("No identity provider found. Access to chat will be blocked.")
        return [];
    }

    const payload = await response.json();
    return payload;
}

// export const fetchChatHistoryInit = async (): Promise<Conversation[] | null> => {
export const fetchChatHistoryInit = (): Conversation[] | null => {
    // Make initial API call here

    // return null;
    return chatHistorySampleData;
}

export const historyList = async (offset: number, length: number, ragIds?: number[], keyword?: string): Promise<Conversation[] | null> => {
    const response = await fetch(`/api/history/list`, {
        method: "POST",
        body: JSON.stringify({
            rag_ids: ragIds !== undefined ? ragIds : [],
            offset: offset,
            length: length,
            keyword: keyword
        }),
        headers: {
            "Content-Type": "application/json"
        },
    }).then(async (res) => {
        const payload = await res.json();
        if (!Array.isArray(payload)) {
            console.error("There was an issue fetching your data.");
            return null;
        }
        // 履歴一覧取得時はメッセージを読み込まない(選択時に読み込む)
        const conversations: Conversation[] = payload.map((conv: any) => {
            const conversation: Conversation = {
                id: conv.id,
                title: conv.title,
                date: conv.createdAt,
                messages: [], // 空の配列で初期化
                rag_ids: conv.ragId,
                is_favorite: conv.is_favorite,
                user_id: conv.userId,
                is_shared: conv?.isShared
            };
            return conversation;
        });
        return conversations;
    }).catch((err) => {
        console.error("There was an issue fetching your data.");
        return null
    })

    return response
}

export const getConversation = async (conversation_id: string): Promise<Conversation | null> => {
    const response = await fetch(`/api/history/conversation/${conversation_id}`, {
        method: "GET",
    })
    if (!response.ok) {
        return null
    }
    const conv = await response.json()
    const conversation: Conversation = {
        id: conv.id,
        title: conv.title,
        date: conv.createdAt,
        messages: [],
        rag_ids: conv.ragId,
        is_favorite: conv.is_favorite,
        user_id: conv.userId,
        is_shared: conv?.isShared
    };

    return conversation
}

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
    const response = await fetch("/api/history/read", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then(async (res) => {
        if(!res){
            return []
        }
        const payload = await res.json();
        let messages: ChatMessage[] = [];
        if(payload?.messages){
            payload.messages.forEach((msg: any) => {
                const message: ChatMessage = {
                    id: msg.id,
                    role: msg.role,
                    date: msg.createdAt,
                    content: msg.content,
                    contentType: msg.contentType,
                    fileName: msg.fileName,
                    file_byte: msg.file_byte,
                    assistantFileID: msg.assistantFileID,
                    user_feedback: msg.user_feedback,
                }
                messages.push(message)
            });
        }
        return messages;
    }).catch((err) => {
        console.error("There was an issue fetching your data.");
        return []
    })
    return response
}

export const historyGetCreateFile = async (blobFileName: string): Promise<string> => {
    const response = await fetch("/api/history/getCreateFile", {
        method: "POST",
        body: JSON.stringify({
            blobFileName: blobFileName
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then(async (res) => {
        if(!res){
            return ""
        }
        const payload = await res.json();
        return payload.file_byte;
    }
    ).catch((err) => {
        console.error("There was an issue fetching your data." + err);
        return ""
    })
    return response
}

export const historyDelete = async (convId: string, mesId?: string) : Promise<Response> => {
    const response = await fetch("/api/history/delete", {
        method: "DELETE",
        body: JSON.stringify({
            conversation_id: convId,
            ...(mesId !== undefined && { message_id: mesId }),
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then((res) => {
        return res
    })
    .catch((err) => {
        console.error("There was an issue fetching your data.");
        let errRes: Response = {
            ...new Response,
            ok: false,
            status: 500,
        }
        return errRes;
    })
    return response;
}
export const historyDeleteAll = async (ragIds: number[]) : Promise<Response> => {
    const response = await fetch(`/api/history/delete_all`, {
        method: "DELETE",
        body: JSON.stringify({
            rag_ids: ragIds,
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then((res) => {
        return res
    })
    .catch((err) => {
        console.error("There was an issue fetching your data.");
        let errRes: Response = {
            ...new Response,
            ok: false,
            status: 500,
        }
        return errRes;
    })
    return response;
}

export const historyRename = async (convId: string, title: string) : Promise<Response> => {
    const response = await fetch("/api/history/rename", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            title: title
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then((res) => {
        return res
    })
    .catch((err) => {
        console.error("There was an issue fetching your data.");
        let errRes: Response = {
            ...new Response,
            ok: false,
            status: 500,
        }
        return errRes;
    })
    return response;
}

export const historyChangeFavorite = async (convId: string, is_favorite: boolean) : Promise<Response> => {
    const response = await fetch("/api/history/changeFavorite", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            is_favorite: is_favorite
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then((res) => {
        return res
    })
    .catch((err) => {
        console.error("There was an issue fetching your data.");
        let errRes: Response = {
            ...new Response,
            ok: false,
            status: 500,
        }
        return errRes;
    })
    return response;
}

export const historyFeedback = async (convId: string, mesId: string, feedback_text: string) => {
    const response = await fetch("/api/history/feedback", {
        method: "POST",
        body: JSON.stringify({
            feedback: feedback_text,
            conversation_id: convId,
            message_id: mesId
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then(async (res) => {
        return res.status == 200
    }).catch((err) => {
        console.error(err);
        return false
    })
    return response
}

export const historyShare = async (convId: string, share: boolean) => {
    const response = await fetch("/api/history/share", {
        method: "POST",
        body: JSON.stringify({
            share: share,
            conversation_id: convId,
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then(async (res) => {
        return res.status == 200
    }).catch((err) => {
        console.error(err);
        return false
    })
    return response
}

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
    const response = await fetch("/api/history/ensure", {
        method: "GET",
    })
    .then(async res => {
        let respJson = await res.json();
        let formattedResponse;
        if(respJson.message){
            formattedResponse = CosmosDBStatus.Working
        }else{
            if(res.status === 500){
                formattedResponse = CosmosDBStatus.NotWorking
            }else{
                formattedResponse = CosmosDBStatus.NotConfigured
            }
        }
        if(!res.ok){
            return {
                cosmosDB: false,
                status: formattedResponse
            }
        }else{
            return {
                cosmosDB: true,
                status: formattedResponse
            }
        }
    })
    .catch((err) => {
        console.error("There was an issue fetching your data.");
        return {
            cosmosDB: false,
            status: err
        }
    })
    return response;
}

export const getGroupDefault = async (): Promise<{rag_flag: boolean, default_model_index: string }> => {
    const response = await fetch(`/api/get_group_default`, {
        method: "GET",
    })
    return await response.json();
}

export const checkMessage = async (values:string): Promise<MessageType> => {
    const response = await fetch("/api/checkmsg", {
        method: "POST",
        body: JSON.stringify({
            msg: values
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then(async res => {
        let respJson = await res.json();
        if(!res.ok){
            if(res.status === 401){
                return {
                    level: 401,
                    word: respJson.message
                }
            }else if(res.status === 412){
                return {
                    level: 412,
                    word: respJson.message
                }
            }
            return {
                level: -1,
                word: respJson.message
            }
        }else{
            return {
                level: respJson.level,
                word: respJson.message
            }
        }
    })
    .catch((err) => {
        console.error("message check err.");
        return {
            level: -1,
            word: err
        }
    })
    return response
}

// 非同期タスクのステータスを取得（汎用）
export async function getTaskStatus(taskId: string): Promise<AsyncTaskStatus> {
    const response = await fetch(`/api/tasks/${taskId}`, {
        method: "GET",
    });
    
    if (!response.ok) {
        throw new Error(`Failed to get task status: ${response.statusText}`);
    }
    
    return await response.json();
}

export function getTaskDownloadUrl(taskId: string): string {
    return `/api/tasks/download/${taskId}`
}

export async function pollAndWaitTask(taskId: string, onProgress?: (progress: number, status: TaskStatus) => void) {
    // ポーリングで進捗を確認
    const pollInterval = 2000; // 2秒ごと
    const maxAttempts = 300; // 最大10分 (2秒 × 300回)
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        
        try {
            const status = await getTaskStatus(taskId);
            
            // 進捗コールバックを呼び出し
            if (onProgress) {
                onProgress(status.progress || 0, status.status);
            }
            
            // 完了時
            if (status.status === 'completed' || status.status === "failed") {
                return status
            }
        } catch (error) {
            console.error("Error polling transcribe status:", error);
            // ポーリングエラーは続行（一時的なネットワークエラーの可能性）
        }
    }
    
    // タイムアウト
}

// 音声ファイル書き起こし（ポーリング対応版）
export async function transcribe(
    audio: File, 
    max_speakers?: number,
    meeting_datetime?: Date,
    onProgress?: (progress: number, status: TaskStatus) => void
): Promise<{result: string, task_id: string}|APIError> {
    const formData = new FormData();
    formData.append("audio", audio);
    if (max_speakers !== undefined && 0 < max_speakers) {
        formData.append("max_speakers", String(max_speakers));
    }

    if(meeting_datetime !== undefined && !Number.isNaN(meeting_datetime.getTime())) {
        const localIso = new Date(meeting_datetime.getTime() - meeting_datetime.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, -1); // toISOStringは必ず末尾ZのUTCとして解釈するため、末尾の'Z'を削除
        formData.append("meeting_datetime", localIso);
    }

    const response = await fetch("/api/transcribe", {
        method: "POST",
        body: formData,
    });
    if (!response.ok) {
        const error_mes = "タスクの開始に失敗しました";
        try{
            const jsoned = await response.json()
            return {
                error: "error" in jsoned ? String(jsoned.error) : error_mes
            }
        }
        catch{
            return {
                error: error_mes
            };
        }
    }

    const startResult = await response.json();
    const taskId = startResult.task_id;

    const pollResult = await pollAndWaitTask(taskId, onProgress)
    if (pollResult === undefined) {
        // タイムアウト
        return {
            error: "音声ファイルの書き起こしがタイムアウトしました。しばらく待ってから再度確認してください。"
        };
    }
    else if (pollResult?.error !== undefined || pollResult.result === undefined) {
        return {
            error: pollResult.error ?? "書き起こしに失敗しました"
        }
    }
    else {
        return {
            result: pollResult.result, task_id: taskId
        }
    }
}

// 利用可能なモデルのインデックスを取得 （とモデル名）
export const getAvailableModels = async (): Promise<Map<string, ModelInfo> | null> => {
    const response = await fetch("/api/available_models", {
        method: "GET",
    })

    if (!response.ok) return null;
    const json: (ModelInfo & {id: string})[] = await response.json();

    const ret = new Map<string, ModelInfo>();
    json.forEach(model => {
        const {id, ...info} = model;
        ret.set(id, info);
    });
    
    return ret;
}

// RAGメニューのリストを取得
export const RAGMenuList = async (): Promise<RAGMenuItem[] | null> => {
    const response = await fetch("/api/ragmenulist", {
        method: "GET",
    })
    .then(async res => {
        const payload = await res.json();
        if (!Array.isArray(payload)) {
            console.error("There was an issue fetching your data.");
            console.error(response);
            return null;
        }
        const ragmenulist: RAGMenuItem[] = await Promise.all(payload.map(async (item: any) => {
            const ragmenu: RAGMenuItem = {
                id: item.seq,
                name: item.container_name,
                prompt_examples: item.prompt_examples,
                tags: item.tags !== null ? Object.entries(item.tags).map(([k, v]) => ({
                    key: k,
                    value: String(v)
                })) : null
            };
            return ragmenu;
        }));
        return ragmenulist;
    })

    return response
}

// RAGメニューの概要を取得
export const RAGMenuSummary = async (rag_index: number): Promise<RAGSummaryInfo> => {
    const response = await fetch(`/api/rag_summary?rag_index=${rag_index}`, {
        method: "GET",
    })
    .then(async res => {
        const payload = await res.json();
        return payload;
    })
    return response
}

// RAGファイルのダウンロードURLを生成
export const getDownloadURL = (rag_id: number, filename: string) => {
    const params = {
        "rag_id": String(rag_id),
        "filename": String(filename)
    }
    const query_params = new URLSearchParams(params)
    return "/api/download_rag_file?" + query_params.toString()
}

export const getCompanyIcon = async (): Promise<string> => {
    const mykey = localStorage.getItem('WorQuforGAI_companykey')
    const response = await fetch(`/api/companyIcon/${mykey}`, {
        method: "GET",
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[getCompanyIcon]');
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        return url;
    })
    .catch((err) => {
        console.error("アイコン取得APIリクエスト例外エラー: ", err);
        return "";
    })
    return response
}

export const getPromptTemplateCategories = async (): Promise<PromptTemplateCategory[]> => {
    const response = await fetch("/api/prompt_template_categories", {
        method: "GET",
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[prompt_template_categories]');
        }
        const json: PromptTemplateCategory[] = await res.json();
        return json;
    })
    .catch((err) => {
        console.error("プロンプトテンプレートカテゴリ取得APIリクエスト例外エラー: ", err);
        return [];
    })
    return response
}

export const getActivePromptTemplateCategoryIds = async (): Promise<(number|null)[]> => {
    const response = await fetch("/api/active_category_ids", {
        method: "GET",
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[active_category_ids]');
        }
        const json: (number|null)[] = await res.json();
        return json;
    })
    .catch((err) => {
        console.error("プロンプトテンプレートカテゴリ取得APIリクエスト例外エラー: ", err);
        return [];
    })
    return response
}

export const getPromptTemplates = async (category_id: number): Promise<PromptTemplate[]> => {
    const response = await fetch(`/api/prompt_templates?category_id=${category_id}`, {
        method: "GET",
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[prompt_templates]');
        }
        const json: PromptTemplate[] = await res.json();
        return json;
    })
    .catch((err) => {
        console.error("プロンプトテンプレート取得APIリクエスト例外エラー: ", err);
        return [];
    })
    return response
}

export const getPromptTemplate = async (template_id: number): Promise<PromptTemplate|undefined> => {
    const response = await fetch(`/api/prompt_templates?template_id=${template_id}`, {
        method: "GET",
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[prompt_templates]');
        }
        const json: PromptTemplate[] = await res.json();
        return json.at(0);
    })
    .catch((err) => {
        console.error("プロンプトテンプレート取得APIリクエスト例外エラー: ", err);
        return undefined;
    })
    return response
}


export const getPromptTemplateGroups = async (template_id: number): Promise<number[]> => {
    const response = await fetch(`/api/prompt_template_groups?template_id=${template_id}`, {
        method: "GET",
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[prompt_templates]');
        }
        return await res.json()
    })
    .catch((err) => {
        console.error("プロンプトテンプレート取得APIリクエスト例外エラー: ", err);
        return [];
    })
    return response
}

export const getIsPublicTemplateCreatable = async (): Promise<boolean> => {
    const response = await fetch(`/api/is_public_template_creatable`, {
        method: "GET"
    })
    .then(async res => {
        return res.ok
    })
    .catch((err) => {
        return false;
    })
    return response
}

export const getAvailableOwners = async (): Promise<string[]> => {
    const response = await fetch(`/api/available_ownerids`, {
        method: "GET"
    })
    .then(async res => {
        const ret: string[] = await res.json()
        return ret
    })
    .catch((err) => {
        return [];
    })
    return response
}

export const getAvailableGroups = async (): Promise<{cd: number, name: string}[]> => {
    const response = await fetch(`/api/available_groups`, {
        method: "GET"
    })
    .then(async res => {
        const ret: Record<string, string> = await res.json()
        return Object.entries(ret).map(([cd, name]) => ({cd: Number(cd), name: name}))
    })
    .catch((err) => {
        return [];
    })
    return response
}

export const addPromptTemplate = async (
    owner_id: string|null, 
    name: string, description: string, category_id: number, template_schema: PromptTemplateSchema,
    newFiles: File[]
): Promise<number|undefined> => {
    const formdata = new FormData()
    formdata.append("mode", "create")
    if (owner_id)
        formdata.append("owner_id", owner_id)
    formdata.append("name", name)
    formdata.append("description", description)
    formdata.append("category_id", String(category_id))
    formdata.append("template_schema", JSON.stringify(template_schema))
    newFiles.forEach((file, index) => {
        formdata.append(`file${index}`, file);
    });
    const response = await fetch(`/api/prompt_templates`, {
        method: "POST",
        body: formdata
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[prompt_templates]');
        }
        return Number(await res.text())
    })
    .catch((err) => {
        return undefined;
    })
    return response
}

export const modifyPromptTemplate = async (
    template_id: number, owner_id: string|null, 
    name: string, description: string, category_id: number, template_schema: PromptTemplateSchema,
    change_owner: boolean, new_owner_id: string|null,
    newFiles: File[]
): Promise<boolean> => {
    const formdata = new FormData()
    formdata.append("mode", "patch")
    formdata.append("template_id", String(template_id))
    if (owner_id)
        formdata.append("owner_id", owner_id)
    formdata.append("name", name)
    formdata.append("description", description)
    formdata.append("category_id", String(category_id))
    formdata.append("template_schema", JSON.stringify(template_schema))
    formdata.append("change_owner", JSON.stringify(change_owner))
    if (change_owner && new_owner_id)
        formdata.append("new_owner_id", new_owner_id)

    newFiles.forEach((file, index) => {
        formdata.append(`file${index}`, file);
    });
    
    const response = await fetch(`/api/prompt_templates`, {
        method: "POST",
        body: formdata
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[prompt_templates]');
        }
        return true
    })
    .catch((err) => {
        return false;
    })
    return response
}

export const deletePromptTemplate = async (template_id: number, owner_id?: string): Promise<boolean> => {
    const formdata = new FormData()
    formdata.append("mode", "delete")
    formdata.append("template_id", String(template_id))
    if (owner_id){
        formdata.append("owner_id", owner_id)
    }
    const response = await fetch(`/api/prompt_templates`, {
        method: "POST",
        body: formdata
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[prompt_templates]');
        }
        return true
    })
    .catch((err) => {
        return false;
    })
    return response
}

export const updatePromptTemplateGroups = async (template_id: number, group_cds: number[]): Promise<boolean> => {
    const formdata = new FormData()
    formdata.append("template_id", String(template_id))
    formdata.append("group_cds", JSON.stringify(group_cds))
    const response = await fetch(`/api/prompt_template_groups`, {
        method: "POST",
        body: formdata
    })
    .then(async res => {
        if (!res.ok) {
            throw new Error('リクエストエラー[prompt_templates]');
        }
        return true
    })
    .catch((err) => {
        console.error("プロンプトテンプレート取得APIリクエスト例外エラー: ", err);
        return false;
    })
    return response
}

export const getLoginInfo = async (): Promise<{ info_list: InfoItem[], info_history: InfoItem[] }> => {
    try {
        const response = await fetch(`/api/login/info`, {
            method: "GET",
        });
        
        if (!response.ok) {
            throw new Error('リクエストエラー[getLoginInfo]');
        }
        
        const data = await response.json();
        return {
            info_list: data.info_list || [],
            info_history: data.info_history || []
        };
    } catch (err) {
        console.error("お知らせ情報取得APIリクエスト例外エラー: ", err);
        return {
            info_list: [],
            info_history: []
        };
    }
}